// Weight-unit conversion helpers. Storage is always grams; oz is a display
// convenience for shop-floor staff who think in ounces.
//
// Business rule: 1 oz = 28 g (whole number). We intentionally do NOT use the
// avoirdupois 28.3495 conversion — the shop floor treats an oz as exactly 28 g
// so the math stays in integers and downstream lot accounting never drifts
// into 28.3-vs-28.35 rounding wars. Mirrors the mobile app (wolfdrive).
export const GRAMS_PER_OZ = 28;
// The warehouse works in pounds. 1 lb = 16 × the 28 g shop-oz = 448 g, so the
// oz/lb math stays in whole grams (¼ lb = 112, ½ lb = 224). Per Umer
// (2026-09-14) — this replaces the 2026-09-01 "real scale pound = 453 g" rule.
// Storage stays canonical grams, so lot/variance accounting is unaffected;
// only what a lb converts to/from changes.
export const GRAMS_PER_LB = 448;
export const GRAMS_PER_KG = 1000;

// Every weight the user can type or toggle between. Canonical storage is
// ALWAYS grams — these only affect what an input displays.
export type WeightUnit = 'g' | 'oz' | 'lb' | 'kg';
export const GRAMS_PER_UNIT: Record<WeightUnit, number> = {
    g: 1,
    oz: GRAMS_PER_OZ,
    lb: GRAMS_PER_LB,
    kg: GRAMS_PER_KG,
};

export const gramsToOz = (g: number): number => g / GRAMS_PER_OZ;
export const ozToGrams = (oz: number): number => oz * GRAMS_PER_OZ;
export const gramsToLb = (g: number): number => g / GRAMS_PER_LB;
export const lbToGrams = (lb: number): number => lb * GRAMS_PER_LB;

// Display helpers — grams already have fmtGrams in warehouseCopy.
export const fmtOz = (oz: number): string => `${oz.toFixed(2)}oz`;

// "8.4 lb" — the warehouse works in pounds, so this is the default weight
// display. Whole pounds render clean ("10 lb"); otherwise up to 2 decimals with
// trailing zeros trimmed ("8.4 lb", "0.33 lb"). Storage stays grams.
export const fmtLb = (g: number): string => {
    const lb = (g || 0) / GRAMS_PER_LB;
    if (Math.abs(lb - Math.round(lb)) < 0.005) return `${Math.round(lb)} lb`;
    return `${lb.toFixed(2).replace(/0+$/, '').replace(/\.$/, '')} lb`;
};

// "1000g (35.71oz)" — for read-only labels that want both. Use sparingly;
// most existing sites should stay grams-only to avoid visual clutter.
export const fmtGramsWithOz = (g: number): string =>
    `${g.toFixed(1)}g (${gramsToOz(g).toFixed(2)}oz)`;

// ─── Pack sizes (inbound lots logged as "N packs of X") ─────────────────────
// Shipments often arrive pre-packed. The WM logs count × size; storage stays
// canonical grams (totalGrams) — these presets exist purely so the modal
// dropdown and display labels agree everywhere. Sizes follow the shop's
// 28 g oz rule above.
export interface PackSize {
    grams: number;
    label: string;      // dropdown label, verbose
    short: string;      // compact display label ("10 × ½ oz")
}

// Full spread from single-gram pre-rolls up to kilo bricks — different
// suppliers pack differently, so the list covers oz fractions, lb fractions
// and metric, with Custom (any weight, any unit) as the escape hatch.
export const PACK_SIZES: PackSize[] = [
    { grams: 1,    label: '1 g',            short: '1 g' },
    { grams: 3.5,  label: '3.5 g (⅛ oz)',   short: '⅛ oz' },
    { grams: 7,    label: '7 g (¼ oz)',     short: '¼ oz' },
    { grams: 14,   label: '14 g (½ oz)',    short: '½ oz' },
    { grams: 28,   label: '28 g (1 oz)',    short: '1 oz' },
    { grams: 112,  label: '112 g (¼ lb)',   short: '¼ lb' },
    { grams: 224,  label: '224 g (½ lb)',   short: '½ lb' },
    { grams: GRAMS_PER_LB, label: `${GRAMS_PER_LB} g (1 lb)`, short: '1 lb' },
    { grams: 500,  label: '500 g (½ kg)',   short: '½ kg' },
    { grams: 1000, label: '1000 g (1 kg)',  short: '1 kg' },
];

export const packSizeShortLabel = (grams: number): string => {
    const preset = PACK_SIZES.find(p => p.grams === grams);
    return preset ? preset.short : `${grams} g`;
};

// "10 × ½ oz" — for lot cards and detail views. Returns '' when the lot
// wasn't logged as packs so callers can render conditionally.
export const fmtPackBreakdown = (packCount?: number, packSizeGrams?: number): string => {
    if (!packCount || !packSizeGrams) return '';
    return `${packCount} × ${packSizeShortLabel(packSizeGrams)}`;
};

// ─── Count units (lots logged by quantity, not weight) ──────────────────────
// Some material is never weighed — bundles of cigarettes arrive as a count.
// Such lots keep the COUNT in totalGrams/remainingGrams so every downstream
// number (assignments, runs, variance) flows through unchanged; `countUnit`
// on the lot flips displays from "2000g" to "50 bundles".
export const COUNT_UNITS: string[] = ['bundles', 'cartons', 'packs', 'pieces'];

// "1 bundle" / "50 bundles" — presets singularize by dropping the final s;
// custom unit names are used as typed.
export const fmtCount = (n: number, unit: string): string => {
    const rounded = Math.abs(n - Math.round(n)) < 0.05 ? String(Math.round(n)) : n.toFixed(1);
    const u = rounded === '1' && COUNT_UNITS.includes(unit) ? unit.replace(/s$/, '') : unit;
    return `${rounded} ${u}`;
};

// "4 boxes × 50 bundles" — for lots logged in Boxes mode. Returns '' when
// the lot wasn't, so callers can render conditionally (like fmtPackBreakdown).
export const fmtBoxBreakdown = (boxCount?: number, unitsPerBox?: number, countUnit?: string): string => {
    if (!boxCount || !unitsPerBox) return '';
    return `${boxCount} ${boxCount === 1 ? 'box' : 'boxes'} × ${unitsPerBox} ${countUnit || 'units'}`;
};
